import React, { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import registry, {
  formatPolicyDate, getPolicy, POLICY_DESCRIPTIONS, POLICY_IDS, policyPath, policyVersions,
} from './policyRegistry';
import PolicyLinks from './PolicyLinks';
import './legal.css';

function InlineText({ text }) {
  return text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https:\/\/[^)\s]+\))/g).map((part, index) => {
    if (part.startsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\((https:\/\/[^)\s]+)\)$/);
    if (link) return <a key={index} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>;
    return part;
  });
}

function PolicyBlock({ block, sectionTitle }) {
  if (block.type === 'list') {
    return <ul>{block.items.map((item, index) => <li key={index}><InlineText text={item} /></li>)}</ul>;
  }
  if (block.type === 'table') {
    return (
      <div className="legal-table-scroll" role="region" aria-label={`${sectionTitle} table`} tabIndex={0}>
        <table>
          <caption className="legal-visually-hidden">{sectionTitle}</caption>
          <thead><tr>{block.headers.map((header) => <th scope="col" key={header}>{header}</th>)}</tr></thead>
          <tbody>{block.rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}><InlineText text={cell} /></td>)}</tr>
          ))}</tbody>
        </table>
      </div>
    );
  }
  return <p><InlineText text={block.text} /></p>;
}

export function DraftNotice() {
  return (
    <aside className="legal-draft" aria-label="Draft policy notice">
      <strong>Draft for review · Not yet in effect</strong>
      <p>These policies are being prepared for launch. They do not change the terms of an existing purchase.</p>
    </aside>
  );
}

export function PolicyMetadata({ document }) {
  return (
    <dl className="legal-meta">
      <div><dt>Version</dt><dd>{document.version}</dd></div>
      <div><dt>Effective date</dt><dd>{document.effectiveDate
        ? <time dateTime={document.effectiveDate}>{formatPolicyDate(document.effectiveDate)}</time>
        : 'Not yet effective'}</dd></div>
      <div><dt>Status</dt><dd>{document.status === 'draft' ? 'Draft' : document.isScheduled ? 'Scheduled' : document.isArchived ? 'Previous version' : 'Current'}</dd></div>
    </dl>
  );
}

export function PolicyDocumentContent({ document, idPrefix = '', headingAs: Heading = 'h2' }) {
  return document.sections.map((section) => {
    const sectionId = `${idPrefix}${section.id}`;
    return (
      <section key={section.id} id={sectionId} aria-labelledby={`${sectionId}-title`}>
        <Heading id={`${sectionId}-title`}>{section.title}</Heading>
        {section.blocks.map((block, index) => <PolicyBlock key={index} block={block} sectionTitle={section.title} />)}
      </section>
    );
  });
}

export default function LegalPage({ homeHref = '/', homeLabel = 'Back to Wedlinx' }) {
  const { policy, version } = useParams();
  const document = policy ? getPolicy(policy, version) : null;
  const headingRef = useRef(null);
  const title = policy ? document?.title || 'Policy not found' : 'Our policies';
  const isDraft = document ? document.status === 'draft' : !registry.currentVersion;
  const selectedVersion = document?.version;

  useEffect(() => {
    const previousTitle = window.document.title;
    window.document.title = `${title}${selectedVersion ? ` · v${selectedVersion}` : ''} | Wedlinx`;
    if (!window.location.hash) headingRef.current?.focus({ preventScroll: true });
    return () => { window.document.title = previousTitle; };
  }, [title, selectedVersion]);

  return (
    <div className="legal-page">
      <a href="#legal-main" className="legal-skip">Skip to policy content</a>
      <header className="legal-header">
        <a className="legal-wordmark" href={homeHref} aria-label="Wedlinx home">wedlinx<span aria-hidden="true">.</span></a>
        <a className="legal-home" href={homeHref}><span aria-hidden="true">← </span>{homeLabel}</a>
      </header>
      <main id="legal-main" className="legal-main">
        <div className="legal-intro">
          <p className="legal-eyebrow">THE DETAILS THAT MATTER</p>
          {policy && <Link className="legal-back" to="/legal">All policies</Link>}
          <h1 ref={headingRef} tabIndex={-1}>{title}</h1>
          {document ? (
            <>
              <p className="legal-summary">{document.summary}</p>
              <PolicyMetadata document={document} />
            </>
          ) : !policy && <p className="legal-summary">How we look after your gallery, your content and your data. Read the details here, whenever you need them.</p>}
        </div>

        {(!policy || document) && isDraft && <DraftNotice />}
        {document?.isScheduled && <aside className="legal-archive-note" aria-label="Scheduled policy notice">This policy takes effect on {formatPolicyDate(document.effectiveDate)}. Until then, existing terms continue to apply.</aside>}

        {!policy ? (
          <>
            <div className="legal-cards">
              {POLICY_IDS.map((id, index) => {
                const latest = getPolicy(id);
                return (
                  <Link className="legal-card" to={policyPath(id)} key={id}>
                    <span className="legal-card-number" aria-hidden="true">0{index + 1}</span>
                    <h2>{latest.title}<span aria-hidden="true"> ↗</span></h2>
                    <p>{POLICY_DESCRIPTIONS[id]}</p>
                    <span className="legal-card-version">Version {latest.version} · {latest.status === 'draft' ? 'Draft' : `Effective ${formatPolicyDate(latest.effectiveDate)}`}</span>
                  </Link>
                );
              })}
            </div>
            <section className="legal-index-note" aria-labelledby="legal-versions-title">
              <h2 id="legal-versions-title">Your terms stay within reach.</h2>
              <p>Every policy has a versioned link. Earlier published versions remain available here so you can return to the terms that applied to your purchase.</p>
            </section>
          </>
        ) : document ? (
          <>
            <nav className="legal-policy-nav" aria-label="Policy documents">
              {POLICY_IDS.map((id) => <Link key={id} to={policyPath(id)} aria-current={id === policy ? 'page' : undefined}>{getPolicy(id).title}</Link>)}
            </nav>
            {document.isArchived && <p className="legal-archive-note">You are reading a previous version. <Link to={policyPath(policy)}>Read the current policy.</Link></p>}
            <div className="legal-document-layout">
              <aside className="legal-contents">
                <details open>
                  <summary>On this page</summary>
                  <nav aria-label="Policy sections">
                    {document.sections.map((section) => <a key={section.id} href={`#${section.id}`}>{section.title}</a>)}
                  </nav>
                </details>
              </aside>
              <article className="legal-document" aria-label={document.title}>
                <PolicyDocumentContent document={document} />
                <nav className="legal-versions" aria-label="Policy versions">
                  <h2>Policy versions</h2>
                  <ul>{policyVersions(policy).map((available) => {
                    const item = getPolicy(policy, available);
                    return <li key={available}><Link to={policyPath(policy, available)} aria-current={available === document.version ? 'page' : undefined}>Version {available}</Link><span> · {item.effectiveDate ? `Effective ${formatPolicyDate(item.effectiveDate)}` : 'Draft · Not yet effective'}</span></li>;
                  })}</ul>
                </nav>
              </article>
            </div>
          </>
        ) : (
          <div className="legal-not-found" role="status">
            <p>We could not find that policy or version. Check the link, or choose a policy below.</p>
            <PolicyLinks newTab={false} />
          </div>
        )}
      </main>
      <footer className="legal-footer">
        <div><strong>A question about these policies?</strong><p>We typically reply within 24 hours.</p></div>
        <div className="legal-contact"><a href="mailto:wedlinx@catalinx.space">wedlinx@catalinx.space</a><a href="https://wa.me/6585277976" target="_blank" rel="noopener noreferrer">WhatsApp +65 8527 7976</a></div>
        <p className="legal-company">Catalinx Pte Ltd · UEN 202441701E · Singapore</p>
      </footer>
    </div>
  );
}
